/**
 * LogoBackingChip — legible surface for user-uploaded customer/company logos
 * across the dark app UI.
 *
 * Problem: uploaded logos are authored for white paper (e.g. a red CLAYCO
 * wordmark, or a black wordmark) and vanish — or lose all contrast — on the
 * near-black app chrome. CSS-inverting a coloured mark (`brightness-0 invert`)
 * distorts the brand (inverted CLAYCO red is wrong), so the safe, brand-correct
 * fix is to paint a subtle ALWAYS-LIGHT backing chip behind the logo in dark
 * mode and render the logo unmodified on top — arbitrary colours then keep
 * their contrast.
 *
 * Theme behaviour (driven entirely by the `--logo-chip-*` tokens in index.css):
 *   - DARK mode  → near-white chip + hairline border (`bg-logo-chip` /
 *     `border-logo-chip`), so a coloured/dark logo reads against it.
 *   - LIGHT mode → both tokens resolve to `transparent`, so the chip collapses
 *     to nothing (the app surface is already light — no unwanted box).
 *
 * The Datacrate inline `BrandLogo` is theme-aware SVG and does NOT use this
 * chip; this primitive is for opaque raster/vector logos we cannot recolour.
 */
import React from 'react';

interface LogoBackingChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** The uploaded-logo <img> (or any element) to seat on the backing chip. */
  children: React.ReactNode;
  /** Extra classes for the chip wrapper (layout / margins from the caller). */
  className?: string;
  /**
   * #120 — site-card treatment: instead of seating the logo on a light backing
   * chip, render it WHITE on a TRANSPARENT background in DARK mode (a CSS filter
   * keyed off `[data-logo-white-on-dark]` in index.css paints the mark white).
   * Light mode is unchanged (logo as-authored, no chip). Off by default so
   * report covers (printed on light) keep the brand-correct chip.
   */
  whiteOnDark?: boolean;
}

export const LogoBackingChip: React.FC<LogoBackingChipProps> = ({
  children,
  className,
  whiteOnDark = false,
  ...rest
}) => (
  <span
    // Padding + rounded corners give the logo breathing room on the chip.
    // Default: a light backing chip (invisible in light mode via transparent
    // tokens) so coloured/dark logos keep their contrast on dark chrome.
    // whiteOnDark: drop the chip and paint the logo white in dark mode instead.
    className={`inline-flex items-center rounded-md px-1.5 py-1 ${
      whiteOnDark ? '' : 'border bg-logo-chip border-logo-chip-edge'
    } ${className ?? ''}`}
    data-logo-chip="true"
    {...(whiteOnDark ? { 'data-logo-white-on-dark': 'true' } : {})}
    {...rest}
  >
    {children}
  </span>
);
